// Первый экран главной — «три двери» (ТЗ владельца 09.10.2026): карьерный
// тест, карта репетиторов и кружков «рядом с домом», учёба за рубежом. Плюс
// карточка для репетиторов с кнопкой «Anket yarat» (ТЗ «два пути»), а СРАЗУ
// ПОД дверями — строка EAS и бесплатная проверка (их передаёт HomePage).
//
// Кнопка карты («Repetitor tap» / «Dərnək tap») работает и без предмета:
// mapQueryHref не пишет пустые поля — адрес без subject=, район передаётся.
//
// Прежний первый экран не удалён: LegacyHero в HomePage.tsx, откат — одна
// строка (HOME_HERO там же).
//
// Честность мини-карты (ТЗ, обязательно):
//   · только настоящие опубликованные анкеты — представление map_partners_public,
//     то же, что читает /tutors (listPublicPartners); демо-точек нет;
//   · счётчик — только при N ≥ MAP_COUNT_MIN, иначе его нет вовсе;
//   · 0 точек по типу/предмету — вместо пустой карты строка и кнопка заявки,
//     открывающая ТУ ЖЕ форму, что на /tutors (RequestForm → submitMapRequest).
//
// Скорость (замеры Lighthouse на превью, 09.10.2026):
//   · фото теста — LCP: <link rel="preload"> в home-head.ts с тем же
//     srcset/sizes (TEST_PHOTO), только 640/960 webp;
//   · данные карты и скрипт Google Maps: на десктопе — после load у зоны
//     видимости; на телефоне/планшете — только когда блок реально на экране
//     или по тапу на плейсхолдер;
//   · синхронно здесь нет ни карты, ни справочника /tutors: MapCanvas и
//     серверные функции — в HomeMapLive (lazy), форма заявки — lazy,
//     справочник для списков — map-options.ts (import() после load или по
//     касанию списка). Пока грузится — плейсхолдер того же размера.
//
// Проверка — scripts/verify-home-doors.mts.
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";

import { DOORS_COPY, MAP_CHIPS, MAP_COUNT_MIN, TEST_PHOTO } from "./doors-copy";
import { ev } from "./home-events";
import type { HomeLang } from "./HomePage";
import type { MapOptions } from "./map-options";
import { STATIC_MAP_SIZE, isRealStaticMap, staticMapUrl } from "./static-map";
import { mapQueryHref } from "@/components/partners/map-href";
import { TUTORS_JOIN_HREF, TUTORS_MAP_HREF } from "@/components/site/SiteChrome";
import { ABROAD_START_HREF } from "@/components/abroad/abroad-copy";
import type { PartnerKind } from "@/data/map-taxonomy";
import type { PublicPartner } from "@/lib/map-public.functions";

const HomeMapLive = lazy(() => import("./HomeMapLive"));
const RequestForm = lazy(() =>
  import("@/components/partners/TutorsMapPage").then((m) => ({ default: m.RequestForm })),
);

const Arrow = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

/** Адрес теста — как у прежней кнопки первого экрана: язык и пакет basic. */
export const doorTestHref = (lang: HomeLang) => `/start?lang=${lang}&pkg=basic`;

/** Точки мини-карты: тип, предмет (если выбран), только точный адрес с координатами. */
export function mapPinRows(
  rows: PublicPartner[],
  layer: PartnerKind,
  subject: string,
): PublicPartner[] {
  return rows.filter(
    (p) =>
      p.kind === layer &&
      (!subject || p.subjects.includes(subject)) &&
      p.address_mode === "building" &&
      p.lat !== null &&
      p.lng !== null,
  );
}

/** Счётчик «N на карте»: null — не показывать (меньше порога). */
export function mapCount(rows: PublicPartner[], layer: PartnerKind): number | null {
  const n = rows.filter((p) => p.kind === layer).length;
  return n >= MAP_COUNT_MIN ? n : null;
}

// ─── загрузка точек: только у зоны видимости и после load ───────────────
// Десктоп (> 980px): после load И первого действия человека (мышь, колесо,
// прокрутка, клавиша), с запасом 200px. Без этого Google Maps первым кадром
// рисует подложку одной картинкой крупнее фото теста — она становится LCP
// (3,1 с против 1,0 на main). После первого действия браузер LCP уже не
// пишет. Телефон и планшет (≤ 980px, одна колонка): только когда блок
// РЕАЛЬНО на экране (без запаса, хотя бы четверть) или по тапу на
// плейсхолдер — до этого ни данных, ни скрипта карты.
const ONE_COLUMN = "(max-width: 980px)";
const FIRST_INPUT = ["pointermove", "pointerdown", "wheel", "scroll", "keydown", "touchstart"];
function useNearViewport(ref: React.RefObject<HTMLElement | null>) {
  const [near, setNear] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node || near) return;
    let io: IntersectionObserver | null = null;
    const oneCol = !!window.matchMedia?.(ONE_COLUMN).matches;
    const observe = () => {
      FIRST_INPUT.forEach((t) => window.removeEventListener(t, observe));
      if (io) return;
      if (typeof IntersectionObserver === "undefined") return setNear(true);
      io = new IntersectionObserver(
        (es) => {
          if (es.some((e) => e.isIntersecting)) {
            setNear(true);
            io?.disconnect();
          }
        },
        oneCol ? { rootMargin: "0px", threshold: 0.25 } : { rootMargin: "200px" },
      );
      io.observe(node);
    };
    const arm = () => {
      if (oneCol) return observe();
      FIRST_INPUT.forEach((t) => window.addEventListener(t, observe, { passive: true }));
    };
    if (document.readyState === "complete") arm();
    else window.addEventListener("load", arm, { once: true });
    return () => {
      window.removeEventListener("load", arm);
      FIRST_INPUT.forEach((t) => window.removeEventListener(t, observe));
      io?.disconnect();
    };
  }, [ref, near]);
  return [near, () => setNear(true)] as const;
}

// ─── опции списков: справочник лениво, после load (или по касанию списка) ──
function useMapOptions(injected: MapOptions | undefined) {
  const [opts, setOpts] = useState<MapOptions | null>(injected ?? null);
  const started = useRef(false);
  const load = () => {
    if (started.current || injected) return;
    started.current = true;
    import("./map-options")
      .then((m) => setOpts(m.MAP_OPTIONS))
      .catch(() => (started.current = false));
  };
  useEffect(() => {
    if (injected) return;
    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number;
    };
    const idle = () =>
      w.requestIdleCallback
        ? w.requestIdleCallback(load, { timeout: 2000 })
        : setTimeout(load, 300);
    if (document.readyState === "complete") idle();
    else window.addEventListener("load", idle, { once: true });
    return () => window.removeEventListener("load", idle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [opts, load] as const;
}

type RowsState = { rows: PublicPartner[] | null; failed: boolean };

function useMapRows(enabled: boolean, injected: PublicPartner[] | undefined): RowsState {
  const [st, setSt] = useState<RowsState>({ rows: injected ?? null, failed: false });
  useEffect(() => {
    if (injected !== undefined || !enabled) return;
    let cancelled = false;
    import("@/lib/map-public.functions")
      .then((m) => m.listPublicPartners())
      .then((r) => !cancelled && setSt({ rows: r.rows, failed: false }))
      // Сбой чтения — не «пусто»: пустое состояние с заявкой не показываем,
      // остаётся спокойный плейсхолдер и ссылка на полную карту.
      .catch(() => !cancelled && setSt({ rows: null, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [enabled, injected]);
  return st;
}

// ─── подложка пустого состояния на телефоне: статичная картинка ─────────
/**
 * Картинка Google Maps Static API (тот же браузерный ключ, центр и зум, что у
 * мини-карты), lazy, с width/height. Ключа нет, Static API не включён или
 * пришла картинка-ошибка (не тот размер) — остаётся нейтральный фон. Тап —
 * живая карта.
 */
export function StaticMapBase({
  lang,
  mapsKey,
  label,
  onTap,
}: {
  lang: HomeLang;
  mapsKey: string | null | undefined;
  label: string;
  onTap: () => void;
}) {
  const [good, setGood] = useState(true);
  return (
    <button
      type="button"
      className="hd-map__ph hd-map__wake hd-static"
      aria-label={label}
      onClick={onTap}
      data-map-static=""
    >
      {mapsKey && good && (
        <img
          src={staticMapUrl(mapsKey, lang)}
          alt=""
          width={STATIC_MAP_SIZE.width}
          height={STATIC_MAP_SIZE.height}
          loading="lazy"
          decoding="async"
          onError={() => setGood(false)}
          onLoad={(e) =>
            !isRealStaticMap(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight) &&
            setGood(false)
          }
        />
      )}
    </button>
  );
}

// ─── дверь 2: карта ──────────────────────────────────────────────────────
export function MapDoor({
  lang,
  rows: injected,
  options: injectedOptions,
  layout,
  mapsKey: injectedKey,
}: {
  lang: HomeLang;
  /** Только для проверки без сети. */
  rows?: PublicPartner[];
  /** Только для проверки: опции списков сразу, без ленивой загрузки. */
  options?: MapOptions;
  /** Только для проверки: раскладка без matchMedia. */
  layout?: "desktop" | "mobile";
  /** Только для проверки: ключ карты без запроса. */
  mapsKey?: string | null;
}) {
  const c = DOORS_COPY[lang].map;
  const [layer, setLayer] = useState<PartnerKind>("tutor");
  const [subject, setSubject] = useState("");
  const [district, setDistrict] = useState("");
  const [reqOpen, setReqOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const [near, wake] = useNearViewport(box);
  const { rows } = useMapRows(near, injected);
  const [opts, loadOpts] = useMapOptions(injectedOptions);
  // Раскладка известна только в браузере; на сервере пустого состояния нет
  // (точки ещё не загружены).
  const [oneCol, setOneCol] = useState<boolean | null>(layout ? layout === "mobile" : null);
  useEffect(() => {
    if (!layout) setOneCol(!!window.matchMedia?.(ONE_COLUMN).matches);
  }, [layout]);
  // Живая карта по тапу (плейсхолдер или статичная подложка).
  const [live, setLive] = useState(false);
  const tap = () => {
    wake();
    setLive(true);
  };
  const [staticKey, setStaticKey] = useState<string | null | undefined>(injectedKey);

  const pins = rows ? mapPinRows(rows, layer, subject) : [];
  const count = rows ? mapCount(rows, layer) : null;
  const empty = rows !== null && pins.length === 0;
  // Ключ для статичной подложки — только на телефоне, только при пустом
  // состоянии и только когда блок уже на экране.
  useEffect(() => {
    if (!(empty && oneCol && near && !live) || staticKey !== undefined) return;
    let cancelled = false;
    import("@/lib/map-partners.functions")
      .then((m) => m.getMapsBrowserKey())
      .then((r) => !cancelled && setStaticKey(r.key))
      .catch(() => !cancelled && setStaticKey(null));
    return () => {
      cancelled = true;
    };
  }, [empty, oneCol, near, live, staticKey]);
  const href = (s: string) => mapQueryHref(TUTORS_MAP_HREF[lang], { layer, subject: s, district });

  const pickLayer = (k: PartnerKind) => {
    setLayer(k);
    setSubject("");
    setReqOpen(false);
  };
  const chip = (code: string) => {
    setSubject(code);
    ev("home_chip_click", { layer, subject: code });
    window.location.assign(href(code));
  };

  let area: ReactNode;
  if (empty) {
    // Подложка — настоящая карта Баку без точек: десктоп — та же ленивая
    // мини-карта (MapCanvas) с пустым списком; телефон — статичная картинка до
    // тапа. Поверх — карточка с текстом и заявкой.
    const ph = <div className="hd-map__ph" aria-hidden="true" />;
    let base: ReactNode = ph;
    if (live || (oneCol === false && near)) {
      base = (
        <Suspense fallback={ph}>
          <HomeMapLive lang={lang} rows={[]} perMonth={c.perMonth} closeLabel={c.close} />
        </Suspense>
      );
    } else if (oneCol) {
      base = (
        <StaticMapBase
          lang={lang}
          mapsKey={staticKey}
          label={c.mapLabel}
          onTap={() => setLive(true)}
        />
      );
    }
    area = (
      <div className="hd-empty" data-map-empty="">
        <div className="hd-empty__base">{base}</div>
        <div className="hd-empty__card" data-map-empty-card="">
          <p>{c.emptyText}</p>
          {reqOpen ? (
            <Suspense fallback={<div className="hd-empty__wait" aria-hidden="true" />}>
              <RequestForm
                lang={lang}
                layer={layer}
                title={c.emptyCta}
                preset={{ subject, grade: "", district }}
                hideTitle
                onSent={() => ev("home_map_request_sent", { layer, subject, district, lang })}
              />
            </Suspense>
          ) : (
            <button
              type="button"
              className="hp-btn"
              onClick={() => {
                setReqOpen(true);
                ev("home_map_request_open", { layer, subject, district, lang });
              }}
            >
              {c.emptyCta}
            </button>
          )}
        </div>
      </div>
    );
  } else if (rows && (near || live)) {
    area = (
      <Suspense fallback={<div className="hd-map__ph" aria-hidden="true" />}>
        <HomeMapLive lang={lang} rows={pins} perMonth={c.perMonth} closeLabel={c.close} />
      </Suspense>
    );
  } else {
    // Статичный плейсхолдер; тап по нему — сразу загрузить карту.
    area = (
      <button
        type="button"
        className="hd-map__ph hd-map__wake"
        aria-label={c.mapLabel}
        data-map-placeholder=""
        onClick={tap}
      />
    );
  }

  return (
    <section className="hd-door hd-mapdoor" aria-labelledby="hd-map-title">
      <div className="hd-mapdoor__head">
        <h2 id="hd-map-title" className="hd-h2">
          {c.title} <em className="hp-serif">{c.titleTail}</em>
        </h2>
        {count !== null && (
          <p className="hd-count" data-map-count="">
            {c.count(count, layer)}
          </p>
        )}
        <p className="hd-hint" data-map-hint="">
          {c.hint[layer]}
        </p>
      </div>
      <div className="hd-seg" role="group" aria-label={c.layerGroup}>
        {(["tutor", "club"] as PartnerKind[]).map((k) => (
          <button key={k} type="button" aria-pressed={layer === k} onClick={() => pickLayer(k)}>
            {c.layers[k]}
          </button>
        ))}
      </div>
      {/* Опции — из справочника /tutors, лениво (map-options.ts); касание
          списка до загрузки запускает её сразу. */}
      <select
        className="hd-in hd-in--subject"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onFocus={loadOpts}
        onPointerDown={loadOpts}
        aria-label={c.subject[layer]}
      >
        <option value="">{c.subject[layer]}</option>
        {opts?.subjects[layer].services.map((x) => (
          <option key={x.code} value={x.code}>
            {x[lang]}
          </option>
        ))}
        {opts?.subjects[layer].groups.map((cat) => (
          <optgroup key={cat.code} label={cat.label[lang]}>
            {cat.items.map((x) => (
              <option key={x.code} value={x.code}>
                {x[lang]}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
      <select
        className="hd-in hd-in--district"
        value={district}
        onChange={(e) => setDistrict(e.target.value)}
        onFocus={loadOpts}
        onPointerDown={loadOpts}
        aria-label={c.district}
      >
        <option value="">{c.district}</option>
        {opts?.districts.map((d) => (
          <option key={d.code} value={d.code}>
            {d[lang]}
          </option>
        ))}
      </select>
      <a
        className="hp-btn hd-search"
        href={href(subject)}
        data-cta="door_map_search"
        data-door-search=""
        onClick={() => ev("home_map_search", { layer, subject, district, lang })}
      >
        {c.search[layer]}
      </a>
      <div className="hd-chips" role="group" aria-label={c.chipsLabel}>
        {MAP_CHIPS[layer].map((ch) => {
          const label = ch.label[lang];
          return (
            <button
              key={ch.code}
              type="button"
              className="hd-chip"
              data-chip={ch.code}
              aria-label={label}
              aria-pressed={subject === ch.code}
              onClick={() => chip(ch.code)}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div className="hd-map" ref={box} role="region" aria-label={c.mapLabel}>
        {area}
        <a
          className="hd-map__full"
          href={TUTORS_MAP_HREF[lang]}
          data-cta="door_map_full"
          onClick={() => ev("home_door_click", { door: "map", lang })}
        >
          {c.openFull}
        </a>
      </div>
    </section>
  );
}

// ─── первый экран целиком ────────────────────────────────────────────────
export function HomeDoors({
  lang,
  proof,
  freeLink,
  freeCheck,
  mapRows,
}: {
  lang: HomeLang;
  /** Строка соцдоказательства EAS — текущий текст главной. */
  proof: string;
  /** «Или бесплатная проверка за минуту» — текущий текст главной. */
  freeLink: string;
  /** Виджет бесплатной проверки — как есть. */
  freeCheck: ReactNode;
  /** Только для проверки без сети. */
  mapRows?: PublicPartner[];
}) {
  const d = DOORS_COPY[lang];
  return (
    <section id="top" className="hp-wrap hd">
      <h1 className="hd-h1">
        {d.h1} <em className="hp-serif">{d.h1Tail}</em>
      </h1>
      <div className="hd-grid">
        <div className="hd-col">
          {/* ДВЕРЬ 1 — тест: карточка кликабельна целиком (растянутая ссылка кнопки) */}
          <article className="hd-door hd-test" data-door="test">
            <div className="hd-test__photo">
              {/* Тот же srcset/sizes, что в <link rel="preload"> (home-head.ts). */}
              <img
                src={TEST_PHOTO.src}
                srcSet={TEST_PHOTO.srcSet}
                sizes={TEST_PHOTO.sizes}
                alt={d.test.photoAlt}
                width={TEST_PHOTO.width}
                height={TEST_PHOTO.height}
                loading="eager"
                fetchPriority="high"
                decoding="async"
              />
              <span className="hd-test__school">European Azerbaijan School</span>
              <span className="hd-test__price">35 ₼</span>
            </div>
            <div className="hd-test__body">
              <h2 className="hd-test__title">
                <span className="hd-long">
                  {d.test.title} <em className="hp-serif">{d.test.titleTail}</em>
                </span>
                <span className="hd-short">{d.test.titleShort}</span>
              </h2>
              <p className="hd-test__sub">
                <span className="hd-long">{d.test.sub}</span>
                <span className="hd-short">{d.test.subShort}</span>
              </p>
              <a
                className="hp-btn hp-btn--lg hd-test__cta"
                href={doorTestHref(lang)}
                data-cta="door_test"
                onClick={() => ev("home_door_click", { door: "test", lang })}
              >
                {d.test.cta} <Arrow />
              </a>
              <a
                className="hd-test__free"
                href="#check"
                data-cta="door_free_check"
                onClick={() => ev("home_free_check_link_click", { lang })}
              >
                {freeLink}
              </a>
            </div>
          </article>

          {/* ДВЕРЬ 3 — зарубежье: сразу старт подбора без кабинета (не лендинг
              и не /cabinet). Пункт меню «Xaricdə təhsil» ведёт на лендинг. */}
          <a
            className="hd-door hd-abroad"
            href={ABROAD_START_HREF[lang]}
            data-door="abroad"
            data-cta="door_abroad"
            onClick={() => ev("home_door_click", { door: "abroad", lang })}
          >
            <img
              className="hd-abroad__photo"
              src="/photos/home/gen-hero-teen-door-480.webp"
              srcSet="/photos/home/gen-hero-teen-door-240.webp 240w, /photos/home/gen-hero-teen-door-480.webp 480w"
              sizes="(max-width: 600px) 116px, 220px"
              alt={d.abroad.photoAlt}
              width={240}
              height={300}
              loading="lazy"
              decoding="async"
            />
            <div className="hd-abroad__body">
              <span className="hd-abroad__badge">{d.abroad.badge}</span>
              <h2 className="hd-abroad__title">{d.abroad.title}</h2>
              <span className="hd-abroad__text">{d.abroad.text}</span>
              <span className="hd-abroad__cta">
                {d.abroad.cta} <Arrow />
              </span>
            </div>
          </a>
        </div>

        <div className="hd-col">
          {/* ДВЕРЬ 2 — карта */}
          <MapDoor lang={lang} rows={mapRows} />

          {/* карточка для репетиторов и кружков: кнопка «Anket yarat» —
              контурная, тише «Testdən keç» на двери теста */}
          <section className="hd-join" aria-labelledby="hd-join-title">
            <img
              className="hd-join__photo"
              src="/photos/home/eas-class-teacher-sq-56.webp"
              srcSet="/photos/home/eas-class-teacher-sq-56.webp 1x, /photos/home/eas-class-teacher-sq-112.webp 2x"
              alt={d.join.photoAlt}
              width={56}
              height={56}
              loading="lazy"
              decoding="async"
            />
            <div className="hd-join__copy">
              <h2 id="hd-join-title" className="hd-join__title">
                {d.join.title}
              </h2>
              <p className="hd-join__text">{d.join.text}</p>
            </div>
            <a
              className="hp-btn hp-btn--outline hd-join__cta"
              href={TUTORS_JOIN_HREF[lang]}
              data-door="tutor_join"
              data-cta="door_tutor_join"
              onClick={() => ev("home_door_click", { door: "tutor_join", lang })}
            >
              {d.join.cta} <Arrow />
            </a>
          </section>
        </div>
      </div>

      {/* сразу под дверями — строка EAS и бесплатная проверка, как были */}
      <div className="hd-below" data-below-doors="">
        <div className="hp-proof">{proof}</div>
        {freeCheck}
      </div>
    </section>
  );
}
